import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import { Toast } from '../../../../shared/Toast';
import { submitVariations } from '../api';
import { VARIATION_FIELDS, emptyVariation, isVariationFilled, subTypeLabel } from '../config';
import ConfirmDialog from './ConfirmDialog';

const cloneVariations = (list) => {
  if (!Array.isArray(list) || !list.length) return [emptyVariation()];
  return list.map((v) => ({
    head_line: v?.head_line || '',
    description: v?.description || '',
    text_in_link: v?.text_in_link || '',
  }));
};

const fieldCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

/** One draft entry in the form. */
const DraftEntry = ({ index, value, onChange, onRemove, removable, busy }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-semibold text-slate-800">Draft {index + 1}</span>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="text-xs font-medium text-slate-400 transition hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Remove
        </button>
      )}
    </div>
    <div className="space-y-2.5">
      {VARIATION_FIELDS.map((f) => (
        <div key={f.key}>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {f.label}
          </label>
          {f.kind === 'textarea' ? (
            <textarea
              rows={3}
              className={`${fieldCls} resize-none`}
              placeholder={f.placeholder}
              value={value[f.key] || ''}
              disabled={busy}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            />
          ) : (
            <input
              type="text"
              className={fieldCls}
              placeholder={f.placeholder}
              value={value[f.key] || ''}
              disabled={busy}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Add/edit the writer's draft variations and submit them for review.
 *
 * Mirrors the designer panel's `UploadDesignModal` in structure — focus
 * capture/restore, scroll lock, Esc handling that yields to a nested
 * `ConfirmDialog` — but there is no file upload here: the deliverable is
 * text, so this is a dynamic form instead of a dropzone.
 *
 * Seeded from `row.variations` (the creative's current drafts) so re-opening
 * this modal edits what's already there instead of starting blank.
 */
const SubmitVariationsModal = ({ open, row, onClose, onSubmitted }) => {
  const [variations, setVariations] = useState([emptyVariation()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setVariations(cloneVariations(row?.variations));
    setError(null);
    setConfirmOpen(false);
  }, [open, row?.bsc_id]);

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const body = document.body;
    const outlet = document.querySelector('.outlet-body');
    const prevBody = body.style.overflow;
    const prevOutlet = outlet ? outlet.style.overflow : '';
    body.style.overflow = 'hidden';
    if (outlet) outlet.style.overflow = 'hidden';
    const focusTimer = setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      clearTimeout(focusTimer);
      body.style.overflow = prevBody;
      if (outlet) outlet.style.overflow = prevOutlet;
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;
  const confirmOpenRef = useRef(confirmOpen);
  confirmOpenRef.current = confirmOpen;

  const requestClose = useCallback(() => {
    if (submittingRef.current) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    // Capture phase: also stops Esc reaching the DetailDrawer's bubble-phase
    // listener behind us. While the confirm dialog is open we bow out
    // entirely (no stopPropagation) so its own capture listener — registered
    // later, therefore called later in the capture phase — gets the key.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmOpenRef.current) return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, requestClose]);

  if (!open || !row) return null;

  const filled = variations.filter(isVariationFilled);
  const ready = filled.length > 0;

  const updateAt = (idx, next) =>
    setVariations((prev) => prev.map((v, i) => (i === idx ? next : v)));

  const addDraft = () => setVariations((prev) => [...prev, emptyVariation()]);

  const removeAt = (idx) =>
    setVariations((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const requestSubmit = () => {
    if (!ready || submitting) return;
    setConfirmOpen(true);
  };

  const status = Number(row.status);
  const confirmMessage =
    status === 4
      ? 'This resubmits your drafts — the rejection is cleared and sales reviews them again.'
      : status === 3
        ? 'This updates your drafts. They stay under review.'
        : 'This submits your drafts for review.';

  const handleSubmit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = filled.map((v) => ({
        head_line: String(v.head_line || '').trim() || undefined,
        description: String(v.description || '').trim() || undefined,
        text_in_link: String(v.text_in_link || '').trim() || undefined,
      }));
      const res = await submitVariations(row.bsc_id, payload);
      if (!mountedRef.current) return;
      Toast('success', 'Drafts submitted for review');
      onSubmitted({
        bsc_id: row.bsc_id,
        status: res?.status ?? row.status,
        logs: res?.logs || [],
        variations: payload,
      });
      setSubmitting(false);
      onClose();
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err?.message || 'Unable to submit your drafts.';
      setError(message);
      Toast('error', message);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Submit drafts"
        tabIndex={-1}
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl outline-none sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">Submit drafts</h2>
            <p className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-500">
              {row.creative_name || `Creative #${row.bsc_id}`} · {subTypeLabel(row.sub_type)}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={submitting}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '18px' }} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
          <p className="flex items-start gap-1.5 text-xs text-slate-500">
            <SlIcon
              library="lucide"
              name="info"
              className="mt-[1px] shrink-0"
              style={{ fontSize: '13px' }}
            />
            <span>
              Write one or more draft versions of the headline, description and link text. Each
              draft needs at least one field filled in.
            </span>
          </p>

          {variations.map((v, i) => (
            <DraftEntry
              key={i}
              index={i}
              value={v}
              onChange={(next) => updateAt(i, next)}
              onRemove={() => removeAt(i)}
              removable={variations.length > 1}
              busy={submitting}
            />
          ))}

          <button
            type="button"
            onClick={addDraft}
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:border-slate-400 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SlIcon library="lucide" name="plus" style={{ fontSize: '14px' }} />
            Add another draft
          </button>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={requestClose}
            disabled={submitting}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={requestSubmit}
            disabled={!ready || submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-300 border-t-white" />
            )}
            {submitting ? 'Submitting…' : error ? 'Retry submit' : 'Submit for review'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Submit these drafts?"
        message={confirmMessage}
        confirmLabel="Submit"
        cancelLabel="Keep editing"
        tone="primary"
        busy={false}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          handleSubmit();
        }}
      >
        <p className="text-xs text-slate-500">
          {filled.length} draft{filled.length === 1 ? '' : 's'} will be sent for review.
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default SubmitVariationsModal;
